import React, { useContext, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { Link, Prompt, withRouter } from "react-router-dom"
import { Alert, Container, Button, Spinner } from "reactstrap"
import { getClubLeadInfo, getClubLeadInfoSuccess } from "./actions"
import { store as socketStore } from "../../../components/Common/Socket/store"
import LeadProfile, { LeadHeader } from "./LeadProfile"
import LeadEditForm from "./LeadEditForm"
import { createLeadDraft, hasLeadChanges, validateLeadDraft } from "./leadDraft"
import { saveClubLead } from "./saveClubLead"
import "../../../assets/scss/custom/pages/_clubAccounts.scss"
import "../../../assets/scss/custom/pages/_clubBusinessLeads.scss"

export const ClubBusinessLeadInfo = ({
  getClubLeadInfo, clubLeadInfo, loading, error, match: { params }, location, session, onLeadSaved,
}) => {
  const operation = useRef(null)
  const correlationId = useRef(null)
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  useEffect(() => {
    setDraft(null)
    setSaveState({ status: "idle" })
    getClubLeadInfo(params.id)
    return () => { operation.current?.cancel(); operation.current = null }
  }, [getClubLeadInfo, params.id])
  const listLocation = { pathname: "/accounts/clubs-leads", search: location.state?.leadListSearch || "" }
  const lead = clubLeadInfo?.id === params.id ? clubLeadInfo : null
  const [draft, setDraft] = useState(null)
  const [errors, setErrors] = useState({})
  const [saveState, setSaveState] = useState({ status: "idle" })
  const dirty = !!(lead && draft && hasLeadChanges(lead, draft))
  const locked = ["pending", "unknown"].includes(saveState.status)
  const shouldBlock = dirty || locked
  const leaveMessage = locked
    ? "The save may still complete. Leave this page and discard your local draft?"
    : "You have unsaved changes. Leave this page and discard the draft?"
  useEffect(() => {
    if (!shouldBlock) return
    const beforeUnload = event => { event.preventDefault(); event.returnValue = "" }
    window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [shouldBlock])
  const startEditing = () => { setDraft(createLeadDraft(lead)); setErrors({}); setSaveState({ status: "idle" }) }
  const save = async (verifyOnly = false) => {
    if (operation.current || (!verifyOnly && (!dirty || locked))) return
    const validation = validateLeadDraft(draft)
    setErrors(validation)
    if (Object.keys(validation).length) {
      document.getElementById(`lead-${Object.keys(validation)[0]}`)?.focus()
      return
    }
    setSaveState({ status: "pending" })
    const current = saveClubLead({ previous: lead, draft, socket, socketClientId, verifyOnly,
      ...(verifyOnly ? { correlationId: correlationId.current } : {}) })
    operation.current = current
    correlationId.current = current.correlationId
    const result = await current.promise
    if (operation.current !== current) return
    operation.current = null
    setSaveState(result)
    if (result.status === "saved") {
      onLeadSaved(result.lead)
      setDraft(null)
    }
  }
  const actions = <>
    {!draft && <Button color="primary" onClick={startEditing}>Edit lead</Button>}
    <Link to={`/accounts/clubs/create/${params.id}`} className="btn btn-outline-secondary">Create club account</Link>
  </>
  return <div className="page-content club-profile-page club-lead-profile"><Container fluid>
    <Prompt when={shouldBlock} message={leaveMessage} />
    <Link to={listLocation} className="btn btn-link lead-back mb-3"><i className="bx bx-arrow-back mr-2" aria-hidden="true" />Back to leads</Link>
    {saveState.status === "saved" && <Alert color="success" role="status">Club lead saved. The displayed details have been verified.</Alert>}
    {loading ? <div role="status" className="lead-detail-state"><Spinner size="sm" /> Loading lead…</div> :
      error ? <div role="alert" className="card card-body lead-detail-state">
        <h1 className="h4">{error.status === 404 ? "Lead not found" : "Could not load lead"}</h1>
        <p>{error.status === 404 ? "This lead does not exist or is no longer available." : "Please try again."}</p>
        <Button color="primary" onClick={() => getClubLeadInfo(params.id)}>Retry</Button>
      </div> : lead ? draft ? <>
        <LeadHeader lead={lead} actions={actions} />
        {saveState.status === "pending" && <Alert color="info" role="status"><Spinner size="sm" className="mr-2" />Saving and verifying the lead…</Alert>}
        {saveState.status === "error" && <Alert color="danger" role="alert">{saveState.message}</Alert>}
        {saveState.status === "unknown" && <Alert color="warning" role="alert">
          <p>The request may still be processing. We could not confirm the saved details. Your draft is kept and will not be submitted again.</p>
          <Button color="warning" onClick={() => save(true)}>Check save status</Button>
        </Alert>}
        {!!Object.keys(errors).length && <Alert color="danger" role="alert">Review the highlighted fields before saving.</Alert>}
        <LeadEditForm draft={draft} onChange={setDraft} settings={session?.settings} errors={errors}
          dirty={dirty} locked={locked} onSave={() => save()} onCancel={() => { setDraft(null); setSaveState({ status: "idle" }) }} />
      </> : <LeadProfile lead={lead} actions={actions} /> : <div role="status" className="lead-detail-state">Loading lead…</div>}
  </Container></div>
}

export default withRouter(connect(({ clubsLeads, session }) => ({
  clubLeadInfo: clubsLeads.clubLeadInfo, loading: clubsLeads.loading, error: clubsLeads.error, session,
}), { getClubLeadInfo, onLeadSaved: getClubLeadInfoSuccess })(ClubBusinessLeadInfo))
